import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail, LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

const field = "w-full bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-white focus:border-[#FF3B30] outline-none transition-colors";

export default function Register() {
  const { register } = useAuth();
  const { settings } = useSettings();
  const logo = settings.logo_image || LOGO_URL;
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success("Account created!");
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[440px] mx-auto px-6 py-16" data-testid="register-page">
      <img src={logo} alt="MTL" className="h-20 w-20 object-contain mx-auto mb-4" />
      <h1 className="font-display text-4xl uppercase tracking-tight text-center">Join The <span className="text-[#FF3B30]">Crew</span></h1>
      <p className="text-zinc-400 text-sm text-center mt-2 mb-8">Create an account to track your gear.</p>
      <form onSubmit={submit} className="space-y-4 border border-zinc-800 bg-zinc-900 p-8">
        {error && <div className="bg-[#FF3B30]/10 border border-[#FF3B30] text-[#FF3B30] text-sm px-4 py-3" data-testid="register-error">{error}</div>}
        <input className={field} placeholder="Full Name" value={form.name} onChange={set("name")} data-testid="register-name" />
        <input className={field} type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="register-email" />
        <div className="relative">
          <input className={field + " pr-12"} type={showPw ? "text" : "password"} placeholder="Password" value={form.password} onChange={set("password")} data-testid="register-password" />
          <button type="button" onClick={() => setShowPw(!showPw)} data-testid="toggle-password-visibility" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
          {loading ? "Creating…" : "Create Account"}
        </button>
      </form>
      <p className="text-center text-sm text-zinc-400 mt-6">Already have one? <Link to="/login" className="text-[#FF3B30] font-bold hover:underline">Log in</Link></p>
    </div>
  );
}
